import type React from "react"
import { ListItem, ListItemAvatar, ListItemText, Avatar, Badge, Divider } from "@mui/material"
import { formatDistanceToNow } from "date-fns"
import { es } from "date-fns/locale"
import type { ConversationDto } from "../../types/message"

const getConversationBackgroundColor = (
  conversation: ConversationDto,
  selectedId: number | null,
  hasUnread: boolean,
): string => {
  if (selectedId !== null && conversation.conversationId === selectedId) {
    return "rgba(76, 175, 80, 0.15)"
  } else if (hasUnread) {
    return "rgba(76, 175, 80, 0.08)"
  }
  return "transparent"
}

interface ConversationItemProps {
  conversation: ConversationDto
  selectedConversationId: number | null
  userProfileId: number | undefined
  onClick: (conversation: ConversationDto) => void
  isLast: boolean
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  selectedConversationId,
  userProfileId,
  onClick,
  isLast,
}) => {
  const otherParticipant =
    conversation.participants.find((p) => p.profileId !== userProfileId) || conversation.participants[0]

  const bgColor = getConversationBackgroundColor(conversation, selectedConversationId, conversation.unreadCount > 0)

  return (
    <>
      <ListItem
        alignItems="flex-start"
        sx={{
          px: 3,
          py: 2,
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          backgroundColor: bgColor,
        }}
        onClick={() => onClick(conversation)}
      >
        <ListItemAvatar>
          <Badge
            color="success"
            variant="dot"
            invisible={conversation.unreadCount === 0}
            overlap="circular"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Avatar
              alt={otherParticipant.fullName}
              src={otherParticipant.profileImage || "/assets/images/profilePicture.png"}
              sx={{ width: 50, height: 50 }}
            />
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: conversation.unreadCount > 0 ? "bold" : "normal" }}>
                {otherParticipant.fullName}
              </span>
              <span style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.75rem" }}>
                {formatDistanceToNow(new Date(conversation.lastActivityAt), {
                  addSuffix: true,
                  locale: es,
                })}
              </span>
            </div>
          }
          secondary={
            <div>
              {conversation.propertyInfo && (
                <div style={{ display: "block", fontWeight: "500", color: "#4caf50" }}>
                  {conversation.propertyInfo.title}
                </div>
              )}
              <div
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  fontWeight: conversation.unreadCount > 0 ? "500" : "normal",
                  color: "rgba(0, 0, 0, 0.6)",
                }}
              >
                {conversation.lastMessage ? conversation.lastMessage.content : "No hay mensajes en esta conversación"}
              </div>
              {conversation.conversationType && (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: "4px",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    backgroundColor:
                      conversation.conversationType === "Solicitud de Alquiler"
                        ? "rgba(33, 150, 243, 0.1)"
                        : "rgba(76, 175, 80, 0.1)",
                    color: conversation.conversationType === "Solicitud de Alquiler" ? "#1976d2" : "#388e3c",
                    fontSize: "0.75rem",
                  }}
                >
                  {conversation.conversationType}
                </span>
              )}
            </div>
          }
        />
        {conversation.unreadCount > 0 && (
          <Badge
            badgeContent={conversation.unreadCount}
            color="success"
            sx={{
              alignSelf: "center",
              ml: 1,
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                height: "20px",
                minWidth: "20px",
              },
            }}
          />
        )}
      </ListItem>
      {!isLast && <Divider component="li" />}
    </>
  )
}

export default ConversationItem
